import { Injectable } from '@angular/core';
import { Log, IShortLogger } from '../services/logger.service';

/**
 * Centralized error handling service for the application.
 * Provides consistent error handling, logging, and recovery options.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private readonly logger: IShortLogger<ErrorHandlerService> = Log.create(ErrorHandlerService);

  constructor() {}

  /**
   * Handle and log errors with context
   *
   * @param error The error object
   * @param context Description of where the error occurred
   * @param isCritical Whether this is a critical error requiring user notification
   * @returns The original error for chaining
   */
  handleError(error: any, context: string, isCritical: boolean = false): Error {
    const errorMessage = error?.message || String(error);

    // Log error with context using appropriate log level
    this.logger.e(`Error in ${context}:`, error);

    // For critical errors, could show a notification to the user
    if (isCritical) {
      // In a real app, you might use a notification service here
      this.logger.f(`CRITICAL ERROR in ${context}: ${errorMessage}`);
    }

    // Convert to Error object if it isn't already
    if (!(error instanceof Error)) {
      return new Error(`${context}: ${errorMessage}`);
    }

    return error;
  }

  /**
   * Execute a function with error handling
   *
   * @param fn The function to execute
   * @param context Description of the operation for error reporting
   * @param fallbackValue Optional value to return if an error occurs
   * @returns The result of the function or fallbackValue if an error occurred
   */
  tryExecute<T>(fn: () => T, context: string, fallbackValue?: T): T {
    try {
      return fn();
    } catch (error) {
      this.handleError(error, context);

      if (fallbackValue !== undefined) {
        return fallbackValue;
      }

      throw error;
    }
  }

  /**
   * Execute an async function with error handling
   *
   * @param fn The async function to execute
   * @param context Description of the operation for error reporting
   * @param fallbackValue Optional value to return if an error occurs
   * @returns A promise with the result or fallbackValue if an error occurred
   */
  async tryExecuteAsync<T>(fn: () => Promise<T>, context: string, fallbackValue?: T): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      this.handleError(error, context);

      if (fallbackValue !== undefined) {
        return fallbackValue;
      }

      throw error;
    }
  }
}
